import { useEffect, useState, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { Grid, Typography, useTheme } from "@mui/material";
import { useStation } from "../../../../providers/StationProvider";
import Surface from "../../../common/core/Surface";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@emberly/zenith-client";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import useIntersectionOnce from "../../../../hooks/useIntersectionOnce";
import OperationsMap from "../../operationsmap/map/OperationsMap";
import { useVehiclesWithFleetMgmt } from "../../../../hooks/useVehiclesWithFleetMgmt";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "relative",
      width: "100%",
      minHeight: "480px",
      height: "65vh",
      "& .map": {
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: theme.spacing(1)
      },
    },
    title: {
      marginBottom: theme.spacing(2)
    },
    link: {
      textDecoration: "none",
      color: "inherit"
    },
    inlineIcon: {
      marginBottom: "-2px"
    },
  })
);

const settings = {
  "activePlugins": [
    "vehicles",
    "mylocations",
    "dispatch"
  ],
  "plugins": {
    "dispatch": {
      "showTags": true,
      "showCause": true,
      "showMissionType": false
    },
    "vehicles": {
      "driverFilter": "",
      "showRoute": true,
      "showTitle": true,
      "showDriverName": true,
      "disableHoverEffect": true
    },
    "mylocations": {
      "warehouses": false,
      "startlocations": false,
      "stations": true,
      "customers": false,
      "contacts": false
    },
    "trafficwarnings": {
      "density": false,
      "warnings": true,
      "cameras": true
    },
    "heatmap": {
      "seasons": [
        "spring",
        "winter",
        "autumn",
        "summer"
      ],
      "days": [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
      ],
      "time": [
        "morning",
        "day",
        "afternoon",
        "night"
      ],
      "cause": "657af859a829d18a5122beba",
      "missionType": ""
    },
    "weather": {
      "nowcast": true,
      "infrared": false
    }
  },
  "styleUrl": "mapbox://styles/mapbox/standard",
  "styleId": "standard"
}

export default function DashboardMapCard(props) {
  const { activeTasks } = props;
  const { classes } = useStyles();
  const containerRef = useRef(null);
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth();


  const { station } = useStation();
  const stationRef = useRef(station);
  stationRef.current = station;
  const hasStation = !!station;

  const [operationsMap, setOperationsMap] = useState(null);

  const [intersectRef, intersected] = useIntersectionOnce();
  const { t } = useTranslation();
  const navigate = useNavigate();


  const vehicles = useVehiclesWithFleetMgmt(activeTasks);

  useEffect(() => {
    const container = containerRef.current;

    if (!!container && hasStation && intersected) {
      let m = new OperationsMap("dash",
        {
          storage: "none",
          settings,
          container,
          station: stationRef.current,
          theme,
          t,
          getAccessToken: getAccessTokenSilently
        }
      );

      setOperationsMap(m);

      //m.on("dispatchLayerHover", setDispatchLayerHoverId);
      m.on("dispatchLayerClick", (taskId) => navigate(`/activemissions/${taskId}`, { relative: "path" }));

      //m.on("popupDialog", setPopupDialog);
      //m.on("loaded", setLoaded);

      return () => {
        m?.destroy();
      }
    }
  }, [hasStation, theme, t, getAccessTokenSilently, intersected]);

  useEffect(() => {
    operationsMap?.updateVehicleData(vehicles);
  }, [vehicles, operationsMap]);


  useEffect(() => {
    operationsMap?.updateDispatchData(activeTasks, []);
  }, [activeTasks, operationsMap]);


  return (
    <Surface xs={12} title={
      <Grid container justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Grid item>
          <Typography variant="h5" className={classes.title}>{t("home:cards:map:title")}</Typography>
        </Grid>
        <Grid item>
          <NavLink to="/map" className={classes.link}>
            <Typography variant="body2" color="info.main" textAlign="center">{t("home:cards:map:link")} <ArrowForwardIcon fontSize="inherit" className={classes.inlineIcon} /></Typography>
          </NavLink>
        </Grid>
      </Grid>
    }>
      <div className={classes.root} ref={intersectRef}>
        <div ref={containerRef} className="map"></div>
      </div>
    </Surface>
  );
}

